import { BodySemi, CaptionRegular } from '../../Text';
import { DataTable } from '../DataTable';
import { DATA_SOURCES } from '@/config/constants';
import DownloadButton from '../../Button/DownloadButton';
import Drawer from '../../Drawer';
import { GOOGLE_METADATA_BY_KEY } from 'utils/metrics';
import InstagramRelatedAggregateDrawerContent from '../InstagramRelatedCategoriesTable/InstagramRelatedAggregateDrawerContent';
import RelatedAggregateDrawerContent from './RelatedAggregateDrawerContent';
import { RelatedHashtagsTableHeadColFilter } from '../DataTable/styledComponents';
import SectionTableFilterChips from '../../SectionTableFilterChips';
import Stack from '../../Stack';
import TableHeading from '../../TableHeading';
import { TableStack } from '../RelatedHashtagsTable/styledComponents';
import { createDefaultColumns } from '../DataTable/utils';
import { downloadItemsAsCSV } from 'utils/formatCSV';
import { filterHealthStats, filterNames } from 'utils/filters';
import { sourceOf } from 'utils/dataSources';
import sortDataByKey from 'utils/sortDataByKey';
import toast from 'react-hot-toast';
import {
    useInstagramRelatedBrandData,
    useTikTokRelatedBrandData,
} from 'utils/hooks/api';
import useRelatedAggregateTableProps from './useRelatedAggregateTableProps.hook';

/** Renaming a `tableName` drops the column choices saved under it. */
const BY_SOURCE = {
    [DATA_SOURCES.TIKTOK]: {
        useDataHook: useTikTokRelatedBrandData,
        DrawerContent: RelatedAggregateDrawerContent,
        tooltipKey: 'relatedBrands',
        tableName: 'related-brands',
        contentKey: 'RelatedBrandsDataTable',
    },
    [DATA_SOURCES.INSTAGRAM]: {
        useDataHook: useInstagramRelatedBrandData,
        DrawerContent: InstagramRelatedAggregateDrawerContent,
        tooltipKey: 'instagramRelatedBrands',
        tableName: 'instagram-related-brands',
        contentKey: 'InstagramRelatedBrandsDataTable',
    },
};

const relatedBrandCell = ({ value, row, css = '' }) => (
    <Stack css={css} $space="-.25rem" $direction="column">
        <CaptionRegular as="div" css="max-width: 100%;">
            {row?.relatedName}
        </CaptionRegular>
        <BodySemi as="div" css="text-transform: capitalize;">
            {value || row?.brandName}
        </BodySemi>
    </Stack>
);

const defaultGetColumnsConfig = (visibleOrderedMetrics) => {
    const columnsConfig = createDefaultColumns(
        [
            'name',
            GOOGLE_METADATA_BY_KEY.price_segmentation,
            ...visibleOrderedMetrics,
        ],
        { showHealthStatLabels: true },
    );
    columnsConfig[0].formatCell = relatedBrandCell;
    return columnsConfig;
};

function RelatedBrandsSectionShell({
    source,
    categories,
    countries,
    week,
    year,
    handleFilter = {
        healthStats: filterHealthStats,
        name: filterNames,
    },
    handleSort = sortDataByKey,
    initialSortOption,
    initialFilterOption = {},
    icon,
    title,
    subtitle,
    hideCount = false,
    tools,
    tooltipKey,
    tooltipExample,
    filename = 'related brands',
    name,
    getColumnsConfig = defaultGetColumnsConfig,
    sortParamKey,
    rowHeight = 70,
    ...restProps
}) {
    const spec = BY_SOURCE[source];
    const { volumeKey } = sourceOf(source) || {};

    const resolvedSortOption = initialSortOption ?? {
        sortBy: volumeKey,
        ascending: false,
    };
    const resolvedTooltipKey = tooltipKey ?? spec.tooltipKey;

    const { query: countQuery } = spec.useDataHook({
        categories: categories || null,
        countries: countries || null,
        week,
        year,
        per_page: 1,
        enabled: !!(week && year),
    });
    const totalRecords = countQuery.isPreviousData
        ? null
        : (countQuery.data?.pages?.[0]?.total_records ?? null);

    const {
        columnsMenu,
        dataReady,
        isLoading,
        noDataMessage,
        rows,
        sortedData,
        filters,
        filtersKey,
        setFilter,
        setFilters,
        sortOption,
        setSortOption,
        sortOptionKey,
        visibleColumnsConfig,
        handleSearchInputChange,
        queryData,
        fetchNextPage,
        isFetchingNextPage,
        isFetching,
        getQueryData,
    } = useRelatedAggregateTableProps({
        // Must stay constant per mounted table: it selects a hook inside.
        source,
        useDataHook: spec.useDataHook,
        brands: undefined,
        categories,
        countries,
        week,
        year,
        handleFilter,
        handleSort,
        initialSortOption: resolvedSortOption,
        initialFilterOption,
        name,
        getColumnsConfig,
        sortParamKey,
        tableName: spec.tableName,
        supportsAffinityGroupFilter: false,
    });

    const { DrawerContent } = spec;
    return (
        <>
            <TableHeading
                icon={icon}
                title={title}
                subtitle={subtitle}
                {...(!hideCount && { count: totalRecords })}
                tooltipKey={resolvedTooltipKey}
                tooltipExample={tooltipExample}
                tools={
                    <>
                        {tools}
                        <DownloadButton
                            disabled={!dataReady}
                            isLoading={isLoading}
                            onClick={() => {
                                toast.promise(
                                    (async () => {
                                        const columnsConfigCopy =
                                            visibleColumnsConfig.map(
                                                (column) => ({ ...column }),
                                            );
                                        const allData = await getQueryData();
                                        downloadItemsAsCSV(
                                            filename,
                                            allData,
                                            columnsConfigCopy,
                                        );
                                    })(),
                                    {
                                        loading: 'Preparing download...',
                                        success: 'Download complete!',
                                        error: 'Error downloading CSV',
                                    },
                                );
                            }}
                            mixpanelMeta={{
                                contentKey: spec.contentKey,
                            }}
                        />
                    </>
                }
            />
            <TableStack>
                <div
                    css={`
                        display: flex;
                        flex-wrap: nowrap;
                        gap: 0.75rem;
                        align-items: flex-start;
                    `}
                >
                    <div css="flex-grow: 1;">
                        <SectionTableFilterChips
                            handleFilter={handleFilter}
                            filters={filters}
                            setFilter={setFilter}
                            setFilters={setFilters}
                            queryData={queryData}
                        />
                    </div>
                    {columnsMenu}
                </div>
                <DataTable
                    isLoading={isLoading}
                    isFetching={isFetching}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                    data={sortedData}
                    dataKey={`d-*-s-${sortOptionKey}-f-${filtersKey}`}
                    noDataMessage={noDataMessage}
                    sortOption={sortOption}
                    setSortOption={setSortOption}
                    onSearchInputChange={handleSearchInputChange}
                    limitSearchWords={3}
                    DrawerComponent={Drawer}
                    TableHeadColFilter={RelatedHashtagsTableHeadColFilter}
                    columns={visibleColumnsConfig}
                    week={week}
                    year={year}
                    rowHeight={rowHeight}
                    renderDrawerContent={(selectedId) => {
                        const selectedRow = selectedId
                            ? rows.find(({ id }) => id === selectedId)
                            : null;
                        if (!selectedRow) return null;

                        return (
                            <DrawerContent
                                entity="brand"
                                selectedRow={selectedRow}
                                week={week}
                                year={year}
                                countries={
                                    selectedRow.countryId
                                        ? [selectedRow.countryId]
                                        : null
                                }
                                categories={
                                    selectedRow.categoryId
                                        ? [selectedRow.categoryId]
                                        : null
                                }
                                relatedBrands={
                                    selectedRow.relatedBrandId
                                        ? [selectedRow.relatedBrandId]
                                        : null
                                }
                                name={`${name}-${selectedRow.name || ''}`}
                                parentName={name}
                            />
                        );
                    }}
                    {...restProps}
                />
            </TableStack>
        </>
    );
}

export default RelatedBrandsSectionShell;
